// T-082 — Driver Niimbot (B1/B18/B21, D11/D101/D110, B3S/B32, ecc.)
//
// Niimbot NON parla TSPL/ZPL/ESC-POS. Usa un protocollo binario proprietario
// reverse-engineered da varie librerie open-source (niimprint, niimblue,
// NiimBlueLib). Questo driver implementa la versione "BLE generica" del
// protocollo, compatibile con i modelli che espongono il service 0xFF00
// con char WRITE 0xFF02 / NOTIFY 0xFF01.
//
// Frame format:
//   [0x55 0x55] [type] [length] [...data] [checksum] [0xAA 0xAA]
//   - checksum = XOR di (type, length, data[0], data[1], ..., data[N-1])
//   - length = data.length (NON inclusi i 2 byte header/footer)
//
// Sequence di stampa standard (semplificata, robusta su B1/B21/D11/D110):
//   1. Heartbeat 0xDC → wake-up + connection check
//   2. SetLabelDensity 0x21 → 1..5 (default 3)
//   3. SetLabelType 0x23 → 1 (gap labels)
//   4. StartPrint 0x01
//   5. StartPagePrint 0x03
//   6. SetDimension 0x13 → height_dots, width_dots (big-endian)
//   7. SetQuantity 0x15 → quantity (big-endian)
//   8. per ogni riga del bitmap:
//        riga vuota → PrintEmptyRow 0x84 (3 byte: row_hi, row_lo, count)
//        riga non vuota → PrintBitmapRow 0x85 (header 5 byte + data)
//   9. EndPagePrint 0xE3
//   10. poll PrintStatus 0xA3 → 0xB3 finché page_printed == quantity
//   11. EndPrint 0xF3
//
// Per ora il driver pollla per 10s (timeout). Se il print status non arriva
// considera la stampa "inviata" e procede comunque (il pacchetto fisico è
// già nella RAM della stampante).

import type {
  PrinterAdapter, PrinterDevice, PrinterSession, PrinterStatus,
} from '@trackeat/shared';
import { renderLabelCanvas, canvasTo1bit } from './labelRaster.js';
import type { LabelTemplate } from '@trackeat/shared';
import {
  bleConnect, bleDisconnect, bleScanPrinters, bleWriteBytes,
  bleWriteWithResponse, bleStartNotifications, bleStopNotifications,
} from './ble-transport.js';
import {
  classicConnect, classicWriteBytes, classicForceDisconnect, classicListPaired,
} from './bt-classic-transport.js';

// CRITICO (scoperto 2026-05-24 dal reverse engineering dell'APK Niimbot):
// l'app ufficiale usa `createInsecureRfcommSocketToServiceRecord` →
// **Bluetooth Classic SPP (RFCOMM, UUID 1101)**, NON BLE GATT.
// Stesso transport di Clabel. Le 24 task precedenti via GATT erano sbagliate
// proprio per questo: il firmware accetta i comandi via GATT ma il bitmap
// no, perché aspetta i dati via il canale RFCOMM. Da qui il "feed lento +
// etichetta bianca" segnalato dall'utente.
import { Capacitor } from '@capacitor/core';
const useSpp = (): boolean => Capacitor.getPlatform() === 'android';

// ─── Comandi (request type bytes) ─────────────────────────────────────────
// 2026-05-24: REVERSE-ENGINEERED dall'app ufficiale Niimbot via btsnoop_hci.log
// completo (Pixel bug report durante una stampa reale con app Niimbot).
const CMD_INIT                = 0xC1; // <-- comando di init che mancava!
const CMD_CMD_A5              = 0xA5; // ? subito dopo init
const CMD_GET_INFO            = 0x40; // subcodes: 08=serial, 0b/0f/0a/07=altri
const CMD_KEEPALIVE           = 0x1A; // payload sempre [0x01] - usato come filler tra comandi
const CMD_SUBSCRIBE           = 0x0B; // 0b 01, 0b 02, 0b 03 in sequenza all'init
const CMD_HEARTBEAT           = 0xDC;
const CMD_SET_LABEL_DENSITY   = 0x21;
const CMD_SET_LABEL_TYPE      = 0x23;
const CMD_START_PRINT         = 0x01;
const CMD_START_PAGE_PRINT    = 0x03;
const CMD_SET_DIMENSION       = 0x13;
const CMD_SET_QUANTITY        = 0x15;
const CMD_PRINT_EMPTY_ROW     = 0x84;
const CMD_PRINT_BITMAP_ROW    = 0x85;
const CMD_END_PAGE_PRINT      = 0xE3;
const CMD_GET_PRINT_STATUS    = 0xA3;
const CMD_END_PRINT           = 0xF3;

// Response types (firmware → app via NOTIFY).
const RESP_PRINT_STATUS       = 0xB3;
const RESP_HEARTBEAT_BASE     = 0xDE; // famiglia 0xDE/DF/B5 a seconda firmware
const RESP_HEARTBEAT_ALT1     = 0xDF;
const RESP_HEARTBEAT_ALT2     = 0xB5;

// ─── Helpers protocollo ───────────────────────────────────────────────────

function buildPacket(type: number, data: Uint8Array | number[]): Uint8Array {
  const payload = data instanceof Uint8Array ? data : new Uint8Array(data);
  let checksum = type ^ payload.length;
  for (let i = 0; i < payload.length; i++) checksum ^= payload[i]!;
  // Layout: header(2) + type(1) + length(1) + payload(N) + checksum(1) + footer(2) = 7 + N
  // BUG FIXATO 2026-05-24: l'array era 6+N e le ultime righe sovrascrivevano
  // il checksum con 0xAA → firmware rifiutava tutti i pacchetti silenziosamente.
  const out = new Uint8Array(7 + payload.length);
  out[0] = 0x55;
  out[1] = 0x55;
  out[2] = type;
  out[3] = payload.length;
  out.set(payload, 4);
  out[4 + payload.length] = checksum & 0xFF;
  out[5 + payload.length] = 0xAA;
  out[6 + payload.length] = 0xAA;
  return out;
}

// Estrae i pacchetti completi (header...footer) da un buffer continuo di byte
// arrivati via NOTIFY (BLE può frammentare/concatenare). Ritorna pacchetti
// estratti + bytes residui per il prossimo round.
function parsePackets(buf: Uint8Array): { packets: Uint8Array[]; rest: Uint8Array } {
  const packets: Uint8Array[] = [];
  let i = 0;
  while (i < buf.length - 5) {
    if (buf[i] === 0x55 && buf[i + 1] === 0x55) {
      const len = buf[i + 3];
      if (len === undefined) break;
      const end = i + 4 + len + 1 + 2; // header(4) + data(len) + crc(1) + footer(2)
      if (end > buf.length) break; // pacchetto non ancora completo
      if (buf[end - 2] === 0xAA && buf[end - 1] === 0xAA) {
        packets.push(buf.subarray(i, end));
        i = end;
        continue;
      }
    }
    i++; // resincronizza
  }
  return { packets, rest: buf.subarray(i) };
}

// ─── Bitmap encoding per Niimbot ──────────────────────────────────────────

// Encoder bitmap row per Niimbot — header 8 byte totali (2 row + 3 popcount + 3 trailing).
// 2026-05-24: REVERSE-ENGINEERED dall'app ufficiale. Il header reale è di
// **8 byte** (non 5): row_hi, row_lo, popcount0, popcount1, popcount2, 0x01, 0x00, 0x00
// Il driver precedente mandava header da 5 byte → firmware confused, output bianco.
// Esempio dall'app:
//   [type=0x85, payload= 00 49 04 00 04 02 00 00 <bitmap...>]
//   →            row=73 pop=(4,0,4) trailing=(02,00,00)
// I 3 byte trailing variano (di solito 01 00 00, talvolta 02 00 00). Per
// semplicità usiamo 01 00 00 (più frequente).
function encodeBitmapRows(packed: Uint8Array, rowBytes: number, height: number): Uint8Array[] {
  const out: Uint8Array[] = [];
  const third = Math.ceil(rowBytes / 3);

  for (let r = 0; r < height; r++) {
    const offset = r * rowBytes;
    const counts = [0, 0, 0];
    for (let i = 0; i < rowBytes; i++) {
      const seg = Math.min(2, Math.floor(i / third));
      let b = packed[offset + i]!;
      b = b - ((b >> 1) & 0x55);
      b = (b & 0x33) + ((b >> 2) & 0x33);
      counts[seg]! += ((b + (b >> 4)) & 0x0F);
    }
    const data = new Uint8Array(8 + rowBytes);
    data[0] = (r >> 8) & 0xFF;
    data[1] = r & 0xFF;
    data[2] = Math.min(255, counts[0]!);
    data[3] = Math.min(255, counts[1]!);
    data[4] = Math.min(255, counts[2]!);
    data[5] = 0x01;
    data[6] = 0x00;
    data[7] = 0x00;
    data.set(packed.subarray(offset, offset + rowBytes), 8);
    out.push(buildPacket(CMD_PRINT_BITMAP_ROW, data));
  }
  return out;
}

// Inverte i bit: TSPL convention (1=bianco) → Niimbot convention (1=nero).
function invertBits(packed: Uint8Array): Uint8Array {
  const out = new Uint8Array(packed.length);
  for (let i = 0; i < packed.length; i++) out[i] = ~packed[i]! & 0xFF;
  return out;
}

// T-082: la testina dei Niimbot più diffusi NON è 400 dot per 50 mm (= 25.4/203).
// I modelli "grandi" (B1, B18, B21, B3S, B32, T8) hanno 384 dot column max.
// I modelli "piccoli" (D11, D101, D110) hanno 192 dot.
// Se mandi setDimension(width > N) il firmware rifiuta il pacchetto e
// "non stampa nulla" — esattamente il sintomo riferito dall'utente.
//
// Strategia: dato un canvas più largo del max del modello, lo ridimensioniamo
// SEMPRE A SINISTRA (= mantiene l'origine 0,0, taglia il bordo dx). Per
// rotoli 50 mm si perdono ~2 mm a destra (= 16 dot) ma stampa.
function detectMaxWidthDots(deviceName: string): number {
  const n = (deviceName ?? '').toUpperCase();
  if (/^(D11|D101|D110)/.test(n)) return 192;
  // B1, B18, B21, B3S, B32, B16, T8, JC, default: 384
  return 384;
}

function clampCanvasWidth(cv: HTMLCanvasElement, maxWidthDots: number): HTMLCanvasElement {
  if (cv.width <= maxWidthDots) return cv;
  const out = document.createElement('canvas');
  out.width = maxWidthDots;
  out.height = cv.height;
  const ctx = out.getContext('2d');
  if (!ctx) return cv;
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, out.width, out.height);
  // Disegna il canvas originale partendo dall'angolo (0,0) → crop dx.
  ctx.drawImage(cv, 0, 0);
  return out;
}

// ─── Adapter ──────────────────────────────────────────────────────────────

interface NiimbotSession extends PrinterSession {
  readonly connection?: 'bluetooth' | 'ble' | 'wifi' | 'usb';
  // T-082: nome del device (es. "B1-A2C3", "D110-XYZ") per inferire la
  // larghezza max della testina (384 vs 192 dot). Persiste nella session
  // dopo connect() così printLabel non deve ri-cercare il nome.
  readonly deviceName?: string;
}

const responseBuffers = new Map<string, Uint8Array>(); // per deviceId
const responseQueues = new Map<string, Uint8Array[]>();

// T-082 trace: log condensato dell'ultimo print, leggibile dalla UI per debug.
// Esposto via getNiimbotTrace(). Si resetta a ogni printLabel.
let lastTrace: string[] = [];
let lastNotifyCount = 0;
const lastReplyTypes = new Set<number>();
function trace(msg: string): void { lastTrace.push(msg); }
export function getNiimbotTrace(): { lines: string[]; notifyCount: number; replyTypes: number[] } {
  return { lines: lastTrace, notifyCount: lastNotifyCount, replyTypes: Array.from(lastReplyTypes) };
}

// T-082: cancella lo stato interno del driver (buffer/queue/notify) per
// forzare una sessione pulita al prossimo print. Da chiamare dopo
// bleDisconnect per evitare stale state quando la BLE si re-connette.
export function clearNiimbotState(deviceId: string): void {
  responseBuffers.delete(deviceId);
  responseQueues.delete(deviceId);
}

async function ensureNotifications(deviceId: string): Promise<void> {
  if (responseBuffers.has(deviceId)) return;
  responseBuffers.set(deviceId, new Uint8Array(0));
  responseQueues.set(deviceId, []);
  await bleStartNotifications(deviceId, (chunk) => {
    lastNotifyCount += 1;
    const cur = responseBuffers.get(deviceId) ?? new Uint8Array(0);
    const merged = new Uint8Array(cur.length + chunk.length);
    merged.set(cur, 0);
    merged.set(chunk, cur.length);
    const { packets, rest } = parsePackets(merged);
    responseBuffers.set(deviceId, rest);
    const queue = responseQueues.get(deviceId) ?? [];
    for (const p of packets) {
      queue.push(p);
      if (p[2] !== undefined) lastReplyTypes.add(p[2]); // track reply types per debug
    }
    responseQueues.set(deviceId, queue);
  });
}

async function awaitReply(
  deviceId: string,
  acceptedTypes: number[],
  timeoutMs = 2000,
): Promise<Uint8Array | null> {
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const queue = responseQueues.get(deviceId) ?? [];
    const idx = queue.findIndex((p) => acceptedTypes.includes(p[2]!));
    if (idx >= 0) {
      const found = queue[idx]!;
      queue.splice(idx, 1);
      responseQueues.set(deviceId, queue);
      return found;
    }
    await new Promise((r) => setTimeout(r, 50));
  }
  return null;
}

// Wrapper transport: su Android usa SPP (RFCOMM) come l'app ufficiale,
// altrimenti fallback BLE GATT writeWithResponse.
async function transportWrite(deviceId: string, packet: Uint8Array): Promise<void> {
  if (useSpp()) {
    await classicWriteBytes(deviceId, packet);
  } else {
    await bleWriteWithResponse(deviceId, packet);
  }
}

async function sendAndOptionallyAwait(
  deviceId: string,
  packet: Uint8Array,
  acceptedReplyTypes: number[] = [],
  timeoutMs = 2000,
): Promise<Uint8Array | null> {
  await transportWrite(deviceId, packet);
  if (acceptedReplyTypes.length === 0) return null;
  // SPP/RFCOMM transport non ha un read pump (bt-classic-transport non
  // espone notify), quindi responseQueues resta vuoto e awaitReply
  // brucerebbe il timeout pieno. Short-circuit: la stampa è fire-and-forget
  // sul ramo SPP, i bitmap escono comunque via classicWriteBytes.
  if (useSpp()) return null;
  return awaitReply(deviceId, acceptedReplyTypes, timeoutMs);
}

export class NiimbotAdapter implements PrinterAdapter {
  readonly driver = 'niimbot' as const;

  async discover(): Promise<PrinterDevice[]> {
    // Su Android cerchiamo nei device già pairati Classic (l'app Niimbot
    // ufficiale usa SPP/RFCOMM, quindi la B1 va pairata via Settings BT).
    if (useSpp()) {
      const paired = await classicListPaired();
      return paired
        .filter((d) => /^(B1|B18|B21|B3|B32|D11|D101|D110|T8|JC)/i.test(d.name))
        .map((d) => ({
          id: d.address, name: d.name, driver: 'niimbot' as const,
          connection: 'bluetooth' as const, address: d.address, model_hint: null,
        }));
    }
    // Fallback BLE per iOS/PWA
    const found = await bleScanPrinters(8_000, { allServices: true });
    return found
      .filter((d) => /^(B1|B18|B21|B3|B32|D11|D101|D110|T8|JC)/i.test(d.name))
      .map((d) => ({
        id: d.deviceId, name: d.name, driver: 'niimbot' as const,
        connection: 'ble' as const, address: d.deviceId, model_hint: null,
      }));
  }

  async connect(device: PrinterDevice): Promise<PrinterSession> {
    if (useSpp()) {
      // SPP path (Android): plugin BluetoothSerial fa createInsecureRfcomm
      // (UUID 1101) come l'app ufficiale Niimbot.
      await classicConnect(device.address);
      return {
        deviceId: device.address, driver: this.driver,
        connection: 'bluetooth', deviceName: device.name,
      } as NiimbotSession;
    }
    // BLE fallback (iOS/PWA)
    await bleConnect(device.address);
    await ensureNotifications(device.address);
    await sendAndOptionallyAwait(
      device.address,
      buildPacket(CMD_HEARTBEAT, [0x04]),
      [RESP_HEARTBEAT_BASE, RESP_HEARTBEAT_ALT1, RESP_HEARTBEAT_ALT2],
      1500,
    );
    return {
      deviceId: device.address, driver: this.driver,
      connection: 'ble', deviceName: device.name,
    } as NiimbotSession;
  }

  async disconnect(_session: PrinterSession): Promise<void> {}

  async forceDisconnect(session: PrinterSession): Promise<void> {
    if (useSpp()) {
      await classicForceDisconnect(session.deviceId);
      return;
    }
    try { await bleStopNotifications(session.deviceId); } catch { /* ignore */ }
    responseBuffers.delete(session.deviceId);
    responseQueues.delete(session.deviceId);
    await bleDisconnect(session.deviceId);
  }

  async status(_session: PrinterSession): Promise<PrinterStatus> {
    return { online: true, paper_ok: true, cover_closed: true };
  }

  async getStatus(_session: PrinterSession): Promise<PrinterStatus> {
    return { online: true, paper_ok: true, cover_closed: true };
  }

  async printLabel(
    session: PrinterSession,
    template: LabelTemplate,
    data: Record<string, unknown>,
    copies = 1,
  ): Promise<void> {
    const sess = session as NiimbotSession;
    lastTrace = [];
    lastNotifyCount = 0;
    lastReplyTypes.clear();
    const cvRaw = renderLabelCanvas(template, data);
    const maxWidth = detectMaxWidthDots(sess.deviceName ?? '');
    const cv = clampCanvasWidth(cvRaw, maxWidth);
    const { bytes: tsplPacked, rowBytes, width, height } = canvasTo1bit(cv);
    // bit polarity: confermata da app ufficiale → bit-1=nero (canvasTo1bit
    // produce bit-1=bianco, quindi invertiamo).
    const packed = invertBits(tsplPacked);

    // Popcount totale del bitmap finale: deve essere > 0 altrimenti il
    // canvas è risultato vuoto (bug rasterizzazione) e il firmware riceve
    // 240 righe tutte vuote → stampa bianca silenziosa.
    let totalBits = 0;
    for (let i = 0; i < packed.length; i++) {
      let b = packed[i]!;
      b = b - ((b >> 1) & 0x55);
      b = (b & 0x33) + ((b >> 2) & 0x33);
      totalBits += ((b + (b >> 4)) & 0x0F);
    }
    trace(`canvas ${width}×${height}, rowBytes=${rowBytes}, bits=${totalBits}`);
    const dev = session.deviceId;

    // ─── INIT SEQUENCE (REVERSE-ENGINEERED dall'app ufficiale 2026-05-24) ───
    // Senza questa sequenza il firmware del B1 ignora qualsiasi comando di
    // stampa. Sequenza estratta dal btsnoop del Pixel durante stampa reale.

    // 1. INIT (mancava nel mio driver!)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_INIT, [0x01]));
    trace('init 0xc1 sent');

    // 2. GET_INFO 0x08 (serial number)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_GET_INFO, [0x08]));

    // 3. CMD_0xa5
    await sendAndOptionallyAwait(dev, buildPacket(CMD_CMD_A5, [0x01]));

    // 4. HEARTBEAT con payload 0x04 (NON 0x01 come avevo!)
    const hb = await sendAndOptionallyAwait(
      dev,
      buildPacket(CMD_HEARTBEAT, [0x04]),
      [RESP_HEARTBEAT_BASE, RESP_HEARTBEAT_ALT1, RESP_HEARTBEAT_ALT2],
      1500,
    );
    trace(`heartbeat 0xdc 04: ${hb ? `reply=0x${hb[2]!.toString(16)}` : 'NO REPLY'}`);

    // 5. GET_INFO subcodes 0x0b, 0x0f (osservati nell'app)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_GET_INFO, [0x0b]));
    await sendAndOptionallyAwait(dev, buildPacket(CMD_GET_INFO, [0x0f]));

    // 6. SUBSCRIBE 0x0b 01/02/03 (sequenza osservata)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_SUBSCRIBE, [0x01]));
    await sendAndOptionallyAwait(dev, buildPacket(CMD_SUBSCRIBE, [0x02]));
    await sendAndOptionallyAwait(dev, buildPacket(CMD_SUBSCRIBE, [0x03]));

    // 7. GET_INFO 0x0a, 0x07
    await sendAndOptionallyAwait(dev, buildPacket(CMD_GET_INFO, [0x0a]));
    await sendAndOptionallyAwait(dev, buildPacket(CMD_GET_INFO, [0x07]));

    trace('init sequence done');

    // ─── PRINT SEQUENCE ───
    // 8. Label type = 1 (gap)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_SET_LABEL_TYPE, [0x01]));
    trace('label_type=gap sent');

    // 9. Density: l'app ufficiale manda 0xfe (osservato). Forse "max-auto".
    await sendAndOptionallyAwait(dev, buildPacket(CMD_SET_LABEL_DENSITY, [0xfe]));
    trace('density=0xfe sent');

    // 10. START_PRINT con payload 7 byte (osservato: 00 01 00 00 00 00 00)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_START_PRINT, [0x00, 0x01, 0x00, 0x00, 0x00, 0x00, 0x00]));
    trace('start_print sent');

    // 11. GET_PRINT_STATUS prima di start_page (osservato)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_GET_PRINT_STATUS, [0x01]));

    // 12. START_PAGE
    await sendAndOptionallyAwait(dev, buildPacket(CMD_START_PAGE_PRINT, [0x01]));
    trace('start_page sent');

    // 13. SET_DIMENSIONS con 6 byte: height_BE, width_BE, quantity_BE
    //     (l'app combina quantity nello stesso pacchetto, non più separato)
    await sendAndOptionallyAwait(dev, buildPacket(CMD_SET_DIMENSION, [
      (height >> 8) & 0xFF, height & 0xFF,
      (width >> 8) & 0xFF, width & 0xFF,
      (copies >> 8) & 0xFF, copies & 0xFF,
    ]));
    trace(`set_dim h=${height} w=${width} qty=${copies} sent (6B)`);

    // Bitmap rows — burst senza delay. L'utente nota che con TrackEAT la
    // carta avanza LENTAMENTE (firmware aspetta righe lente da BLE GATT)
    // mentre con app ufficiale (L2CAP CoC) è veloce → forse il firmware
    // ha un timeout-per-riga. Bombardiamo i pacchetti.
    const rowPackets = encodeBitmapRows(packed, rowBytes, height);
    trace(`bitmap rows: ${rowPackets.length} pkt (WnR burst)`);
    let sentRows = 0;
    for (const pkt of rowPackets) {
      try {
        await transportWrite(dev, pkt);
        sentRows += 1;
      } catch (e) {
        trace(`row ${sentRows} ERR: ${(e as Error).message}`);
        break;
      }
    }
    trace(`rows sent: ${sentRows}/${rowPackets.length}`);

    // 9. End page
    await sendAndOptionallyAwait(dev, buildPacket(CMD_END_PAGE_PRINT, [0x01]));
    trace('end_page sent');

    // 10. Poll status: aspetta che il firmware confermi "stampato X di Y".
    //     Timeout 10s — se non arriva, considera la stampa "inviata" (è
    //     comunque già nel buffer della stampante).
    //     Su SPP/RFCOMM responseQueues non viene mai popolato (no read pump),
    //     quindi il poll brucerebbe 10s di dead time inutile: skip totale.
    if (!useSpp()) {
      const pollStart = Date.now();
      while (Date.now() - pollStart < 10_000) {
        await sendAndOptionallyAwait(
          dev,
          buildPacket(CMD_GET_PRINT_STATUS, [0x01]),
          [RESP_PRINT_STATUS],
          500,
        );
        const queue = responseQueues.get(dev) ?? [];
        const status = queue.find((p) => p[2] === RESP_PRINT_STATUS);
        if (status) {
          // data[0..1] = pagina corrente, data[2..3] = quantity, data[4] = progress %
          const dataStart = 4;
          const pageHi = status[dataStart]!;
          const pageLo = status[dataStart + 1]!;
          const page = (pageHi << 8) | pageLo;
          if (page >= copies) break;
        }
        await new Promise((r) => setTimeout(r, 300));
      }
    }

    // 11. End print
    await sendAndOptionallyAwait(dev, buildPacket(CMD_END_PRINT, [0x01]));
    const replyTypesHex = Array.from(lastReplyTypes).map((t) => '0x' + t.toString(16)).join(',');
    trace(`end_print sent · notify_count=${lastNotifyCount} · reply_types=[${replyTypesHex}]`);
  }
}
